import { useMemo, useState } from "react";
import {
  errorMessage,
  useGlobalSettings,
  usePatchGlobalSettings,
  useTargets,
  type SettingValue,
} from "@/api";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { SettingsForm } from "@/components/SettingsForm";
import { useDocumentTitle } from "@/components/useDocumentTitle";
import {
  describeFallback,
  formatValue,
  globalOverrides,
  groupSpec,
  humanize,
  resolve,
  touchesKey,
} from "@/lib/settings";
import { Card, Skeleton, Tabs, useToast } from "@/ui";
import "./pages.css";

/** Keys rendered as a picker of the owner's channels instead of free text. */
const CHANNEL_PICKER_KEYS = new Set(["alert_channel"]);

export function GlobalSettingsPage() {
  useDocumentTitle("Global settings");
  const settings = useGlobalSettings();
  const targets = useTargets();
  const patch = usePatchGlobalSettings();
  const toast = useToast();
  const [tab, setTab] = useState<string | undefined>();

  const spec = settings.data?.spec;
  const resolved = useMemo(
    () => (spec ? resolve(spec, globalOverrides(settings.data)) : null),
    [spec, settings.data],
  );
  const groups = spec ? groupSpec(spec) : [];
  const active = tab ?? groups[0]?.group;

  const channelOptions = useMemo(
    () => [
      { value: "", label: "Not set" },
      ...(targets.data?.items ?? []).map((t) => ({ value: t.id, label: `#${t.name}` })),
    ],
    [targets.data],
  );

  const alertChannelMissing = resolved && !resolved.effective.alert_channel;

  const save = async (body: { set?: Record<string, SettingValue>; reset?: string[] }, done: string) => {
    try {
      await patch.mutateAsync(body);
      toast.show({ tone: "success", title: done });
    } catch (e) {
      toast.show({ tone: "danger", title: "Could not save", message: errorMessage(e) });
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Global settings"
        description="Defaults for every mapping. Servers and single mappings can override most of them."
      />
      <ErrorBanner error={settings.error} onRetry={() => settings.refetch()} />
      {alertChannelMissing ? (
        <div className="banner banner--warning" role="status">
          No alert channel is set. Choose one under Alerts so the watchdog can reach you.
        </div>
      ) : null}

      <Card>
        {spec && resolved ? (
          <Tabs
            label="Setting groups"
            value={active}
            onChange={setTab}
            items={groups.map((g) => ({
              id: g.group,
              label: g.label,
              content: (
                <SettingsForm
                  spec={spec}
                  level="global"
                  include={(k) => spec[k].group === g.group}
                  row={(key) => ({
                    value: resolved.effective[key],
                    origin: resolved.origin[key],
                    editable: true,
                  })}
                  optionsFor={(key) => (CHANNEL_PICKER_KEYS.has(key) ? channelOptions : undefined)}
                  inheritedLabel={(key) => describeFallback(spec[key].default, "default")}
                  onSet={(key, value) =>
                    CHANNEL_PICKER_KEYS.has(key) && value === ""
                      ? save({ reset: [key] }, `${humanize(key)} cleared`)
                      : save({ set: { [key]: value } }, `${humanize(key)}: ${formatValue(value)}`)
                  }
                  onReset={(key) => save({ reset: [key] }, `${humanize(key)} reset to default`)}
                  saving={(k) =>
                    patch.isPending && touchesKey(k, patch.variables?.set, patch.variables?.reset)
                  }
                />
              ),
            }))}
          />
        ) : (
          <Skeleton lines={12} />
        )}
      </Card>
    </div>
  );
}
